import { useState } from 'react';
import { REACTIONS, type GuestMe, type PublicQueueItem } from '@karozik/shared';
import { Artwork, Avatar, cn, Equalizer, ToastStack, type PartyConnection } from '@karozik/ui';
import { BlindtestOverlay } from './BlindtestOverlay';
import { FacesOverlay } from './FacesOverlay';
import { MomentBanner } from './MomentBanner';
import { PhotosCard } from './Photos';
import { SearchSheet } from './SearchSheet';
import { SelfieCard } from './SelfieCard';

export interface SearchAuth {
  joinToken?: string;
  kioskGuestToken?: string | null;
}

/**
 * The guest's remote control. Deliberately short: what plays, what's next, a
 * button to propose and a row of reactions — then the phone goes back in the
 * pocket.
 */
export function Party({
  conn,
  me,
  search,
  variant,
  header,
}: {
  conn: PartyConnection;
  me: GuestMe | null;
  search: SearchAuth;
  variant: 'phone' | 'kiosk';
  header?: React.ReactNode;
}) {
  const { state } = conn;
  const [searching, setSearching] = useState(false);
  const [localToasts, setLocalToasts] = useState<{ key: number; kind: 'error' | 'success'; message: string }[]>([]);
  const kiosk = variant === 'kiosk';

  const notify = (kind: 'error' | 'success', message: string) => {
    const key = Date.now() + Math.random();
    setLocalToasts((l) => [...l, { key, kind, message }]);
    setTimeout(() => setLocalToasts((l) => l.filter((t) => t.key !== key)), 4000);
  };

  if (!state) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-ink-dim">
        {conn.error ? `Connexion impossible (${conn.error})` : 'Connexion à la soirée…'}
      </div>
    );
  }

  const np = state.nowPlaying;
  const mine = state.queue.filter((q) => q.proposer?.id === me?.id && q.source === 'guest');
  const canSkip = !!np && np.source !== 'moment' && !np.pinned && state.event.status === 'live';
  const skipVoted = !!me && !!np?.skipVoters.includes(me.id);

  return (
    <div className={cn('mx-auto flex min-h-dvh flex-col', kiosk ? 'max-w-3xl px-8 pt-6 pb-40' : 'max-w-md px-4 pt-4 pb-44')}>
      <ToastStack toasts={[...conn.toasts, ...localToasts]} onDismiss={(k) => (conn.dismissToast(k), setLocalToasts((l) => l.filter((t) => t.key !== k)))} />

      {header ?? (
        <header className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-semibold tracking-widest text-ink-faint uppercase">{state.event.name}</div>
            <div className="mt-0.5 flex items-center gap-2 text-sm text-ink-dim">
              <span className="rounded-full bg-surface-2 px-2 py-0.5">
                {state.phase.emoji} {state.phase.name}
              </span>
              <span>👥 {state.guestsOnline}</span>
              {!conn.connected && <span className="text-sun">· reconnexion…</span>}
            </div>
          </div>
          {me && (
            <div className="flex items-center gap-2 rounded-full bg-surface py-1 pr-3 pl-1">
              <Avatar guest={me} size="sm" />
              <span className="max-w-24 truncate text-sm font-semibold">{me.pseudo}</span>
            </div>
          )}
        </header>
      )}

      <MomentBanner state={state} />

      {/* Now playing */}
      <section className="relative mt-4 overflow-hidden rounded-3xl bg-surface">
        {np?.track.artworkUrl && (
          <img src={np.track.artworkUrl} alt="" className="absolute inset-0 h-full w-full scale-125 object-cover opacity-35 blur-2xl" />
        )}
        <div className={cn('relative flex gap-4', kiosk ? 'p-6' : 'p-4')}>
          <Artwork track={np?.track ?? null} className={kiosk ? 'h-40 w-40' : 'h-28 w-28'} />
          <div className="min-w-0 flex-1 self-center">
            <div className="flex items-center gap-2 text-xs font-bold tracking-widest text-pink uppercase">
              <Equalizer playing={!state.paused && !!np} className="text-pink" /> {state.paused ? 'En pause' : 'En ce moment'}
            </div>
            {np ? (
              <>
                <div className={cn('font-display mt-1 leading-tight font-bold', kiosk ? 'text-3xl' : 'text-xl')}>{np.track.title}</div>
                <div className="truncate text-ink-dim">{np.track.artist}</div>
                <ProposedBy item={np} me={me} />
              </>
            ) : (
              <div className="mt-1 text-ink-dim">La musique va démarrer…</div>
            )}
          </div>
        </div>
        {np?.dedication && (
          <div className="relative mx-4 mb-4 rounded-2xl bg-white/10 px-4 py-2 text-sm italic">
            💌 « {np.dedication} »
          </div>
        )}
        {canSkip && state.staff === undefined && (
          <div className="relative flex items-center justify-between border-t border-white/10 px-4 py-2.5 text-sm">
            <span className="text-ink-dim">
              Bof ? {np!.skipVoters.length}/{np!.skipNeeded} pour passer
            </span>
            <button
              onClick={async () => {
                const r = await conn.send('skipVote', { value: !skipVoted });
                if (!r.ok) notify('error', r.error!);
              }}
              className={cn('rounded-full px-3 py-1.5 font-semibold transition', skipVoted ? 'bg-danger text-white' : 'bg-white/10')}
            >
              👎 {skipVoted ? 'Voté' : 'Passer'}
            </button>
          </div>
        )}
      </section>

      {!kiosk && search.joinToken && me && <SelfieCard joinToken={search.joinToken} />}
      {!kiosk && search.joinToken && me && <PhotosCard joinToken={search.joinToken} photoCount={state.photoCount} className="mt-4" />}

      {/* Up next */}
      <section className="mt-6">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="font-display text-lg font-bold">À suivre</h2>
          {me && (
            <span className="text-xs text-ink-faint">
              {mine.length > 0 ? `${mine.length} de tes titres en attente` : 'Aucun titre de toi en attente'}
            </span>
          )}
        </div>
        <ol className="flex flex-col gap-2">
          {state.queue.slice(0, kiosk ? 8 : 12).map((item, i) => (
            <QueueRow key={item.id} item={item} index={i} me={me} big={kiosk} onVote={async (value) => {
              const r = await conn.send('vote', { itemId: item.id, value });
              if (!r.ok) notify('error', r.error!);
            }} />
          ))}
          {state.queue.length === 0 && <li className="rounded-2xl bg-surface px-4 py-6 text-center text-ink-dim">La file est vide : propose le prochain titre !</li>}
        </ol>
      </section>

      {state.recent.length > 0 && !kiosk && (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-semibold tracking-widest text-ink-faint uppercase">Déjà passés</h2>
          <ul className="flex flex-col gap-1.5 opacity-70">
            {state.recent.slice(0, 5).map((r) => (
              <li key={r.id} className="flex items-center gap-3 text-sm">
                <Artwork track={r.track} className="h-8 w-8" rounded="rounded-lg" />
                <span className="truncate">
                  {r.track.title} <span className="text-ink-faint">— {r.track.artist}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Bottom bar: reactions + propose */}
      <div className={cn('glass fixed inset-x-0 bottom-0 z-30 rounded-t-3xl px-4 pt-3 safe-bottom', kiosk && 'px-8 pt-5')}>
        <div className={cn('mx-auto flex flex-col gap-3', kiosk ? 'max-w-3xl' : 'max-w-md')}>
          <div className="flex justify-between">
            {REACTIONS.map((emoji) => (
              <ReactionButton key={emoji} emoji={emoji} big={kiosk} onReact={() => conn.socket?.emit('react', { emoji })} />
            ))}
          </div>
          <button
            onClick={() => setSearching(true)}
            disabled={!me}
            className={cn(
              'bg-gradient-neon w-full rounded-2xl font-bold shadow-xl shadow-pink/25 transition active:scale-[0.98] disabled:opacity-40',
              kiosk ? 'py-5 text-2xl' : 'py-4 text-lg',
            )}
          >
            ＋ Proposer un titre
          </button>
        </div>
      </div>

      {searching && (
        <SearchSheet
          auth={search}
          big={kiosk}
          onClose={() => setSearching(false)}
          onPropose={async (trackId, dedication) => {
            const r = await conn.send('propose', { trackId, dedication });
            if (r.ok) {
              notify('success', '✅ C’est dans la file !');
              setSearching(false);
            }
            return r;
          }}
        />
      )}

      <BlindtestOverlay conn={conn} me={me} big={kiosk} />
      <FacesOverlay conn={conn} me={me} big={kiosk} joinToken={kiosk ? undefined : search.joinToken} />
    </div>
  );
}

function ProposedBy({ item, me }: { item: PublicQueueItem; me: GuestMe | null }) {
  if (!item.proposer) {
    const label = item.source === 'moment' ? '✨ Moment spécial' : item.source === 'admin' ? '🎛️ L’organisateur' : '🤖 Autopilote';
    return <div className="mt-1.5 text-xs text-ink-faint">{label}</div>;
  }
  return (
    <div className="mt-1.5 flex items-center gap-1.5 text-xs text-ink-dim">
      <Avatar guest={item.proposer} size="sm" />
      {item.proposer.id === me?.id ? <b className="text-lime">Ta proposition</b> : <>Proposé par <b>{item.proposer.pseudo}</b></>}
    </div>
  );
}

function QueueRow({ item, index, me, big, onVote }: { item: PublicQueueItem; index: number; me: GuestMe | null; big: boolean; onVote(v: boolean): void }) {
  const voted = !!me && item.voters.includes(me.id);
  const own = item.proposer?.id === me?.id;
  return (
    <li className={cn('flex items-center gap-3 rounded-2xl bg-surface', big ? 'p-3' : 'p-2.5', own && 'ring-1 ring-lime/50')}>
      <span className="w-5 text-center text-sm font-bold text-ink-faint">{index + 1}</span>
      <Artwork track={item.track} className={big ? 'h-16 w-16' : 'h-12 w-12'} rounded="rounded-xl" />
      <div className="min-w-0 flex-1">
        <div className={cn('truncate font-semibold', big && 'text-lg')}>
          {item.pinned && '📌 '}
          {item.track.title}
        </div>
        <div className="truncate text-sm text-ink-dim">{item.track.artist}</div>
        <ProposedBy item={item} me={me} />
      </div>
      {item.source === 'guest' && !own && me ? (
        <button
          onClick={() => onVote(!voted)}
          aria-pressed={voted}
          className={cn(
            'flex min-w-14 flex-col items-center rounded-xl px-2 py-1.5 text-sm font-bold transition active:scale-90',
            voted ? 'bg-pink text-white' : 'bg-surface-2 text-ink-dim',
          )}
        >
          <span className="text-lg leading-none">{voted ? '❤️' : '🤍'}</span>
          {item.votes}
        </button>
      ) : item.votes > 0 ? (
        <span className="min-w-14 text-center text-sm text-ink-dim">❤️ {item.votes}</span>
      ) : null}
    </li>
  );
}

function ReactionButton({ emoji, onReact, big }: { emoji: string; onReact(): void; big: boolean }) {
  const [bump, setBump] = useState(0);
  return (
    <button
      onClick={() => {
        onReact();
        setBump((b) => b + 1);
      }}
      className={cn('relative flex items-center justify-center rounded-2xl bg-surface-2 transition active:scale-90', big ? 'h-20 w-20 text-5xl' : 'h-12 w-12 text-2xl')}
      aria-label={`Réagir ${emoji}`}
    >
      <span key={bump} className={bump ? 'animate-pop' : undefined}>
        {emoji}
      </span>
    </button>
  );
}
